import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { LuArrowLeft, LuCalendar, LuPlus, LuTrash2, LuExternalLink, LuClock } from "react-icons/lu";
import { FaGoogle } from "react-icons/fa";
import { API } from "../App";
import { format, addHours } from "date-fns";
import { es } from "date-fns/locale";

const CalendarPage = ({ auth }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newEvent, setNewEvent] = useState({
    start_time: "",
    end_time: "",
    attendee_email: ""
  });

  const [connections, setConnections] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState("");
  const [selectedCalendarId, setSelectedCalendarId] = useState("primary");

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [selectedConnection, selectedCalendarId]);

  useEffect(() => {
    if (selectedConnection) {
      fetchCalendars(selectedConnection);
    }
  }, [selectedConnection]);

  const fetchConnections = async () => {
    try {
      const response = await fetch(`${API}/calendar/connections`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConnections(data);
        if (data.length > 0) {
          setSelectedConnection(data[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching connections:", error);
    }
  };

  const fetchCalendars = async (connId) => {
    try {
      const response = await fetch(`${API}/calendar/list?connection_id=${connId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCalendars(data);
      }
    } catch (error) {
      console.error("Error fetching calendars:", error);
    }
  };

  const fetchEvents = async () => {
    if (!selectedConnection) return;
    try {
      setLoading(true);
      const url = `${API}/calendar/events?connection_id=${selectedConnection}&calendar_id=${selectedCalendarId}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      toast.error("Error al cargar eventos");
    } finally {
      setLoading(false);
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const response = await fetch(`${API}/oauth/calendar/login`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const data = await response.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      toast.error("Error al conectar");
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = `${API}/calendar/events?connection_id=${selectedConnection}&calendar_id=${selectedCalendarId}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(newEvent)
      });

      if (response.ok) {
        const created = await response.json();
        setEvents([...events, created]);
        setNewEvent({ summary: "", description: "", start_time: "", end_time: "", attendee_email: "" });
        setDialogOpen(false);
        toast.success("Evento creado");
      } else {
        toast.error("Error al crear evento");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("¿Eliminar este evento?")) return;

    try {
      const response = await fetch(`${API}/calendar/events/${eventId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });

      if (response.ok) {
        setEvents(events.filter(e => e.id !== eventId));
        toast.success("Evento eliminado");
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const formatEventTime = (timeStr) => {
    try {
      return format(new Date(timeStr), "HH:mm", { locale: es });
    } catch {
      return timeStr;
    }
  };

  const formatEventDate = (timeStr) => {
    try {
      return format(new Date(timeStr), "d MMM yyyy", { locale: es });
    } catch {
      return timeStr;
    }
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const setDefaultTimes = () => {
    const now = new Date();
    now.setMinutes(0);
    now.setSeconds(0);
    const start = addHours(now, 1);
    const end = addHours(start, 1);
    setNewEvent({
      ...newEvent,
      start_time: start.toISOString().slice(0, 16),
      end_time: end.toISOString().slice(0, 16)
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="rounded-xl" data-testid="back-btn">
                <LuArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <LuCalendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg text-foreground">Google Calendar</h1>
                <p className="text-xs text-muted-foreground">Gestiona tus citas</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {connections.length > 0 && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:mr-4">
                <Select
                  value={selectedConnection}
                  onValueChange={setSelectedConnection}
                >
                  <SelectTrigger className="w-full sm:w-[200px] h-9 bg-muted/50 border-white/5 rounded-lg text-xs">
                    <SelectValue placeholder="Seleccionar cuenta" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {connections.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedCalendarId}
                  onValueChange={setSelectedCalendarId}
                >
                  <SelectTrigger className="w-full sm:w-[180px] h-9 bg-muted/50 border-white/5 rounded-lg text-xs">
                    <SelectValue placeholder="Calendario" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {calendars.map(cal => (
                      <SelectItem key={cal.id} value={cal.id}>
                        {cal.summary}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedConnection && (
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (open) setDefaultTimes();
              }}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-primary hover:bg-primary/90 glow-primary" data-testid="create-event-btn">
                    <LuPlus className="w-5 h-5 mr-2" />
                    Nuevo Evento
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-foreground">Crear Evento</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Título</Label>
                      <Input
                        placeholder="Ej: Reunión con cliente"
                        className="bg-muted/50 border-transparent focus:border-primary rounded-xl"
                        value={newEvent.summary}
                        onChange={(e) => setNewEvent({ ...newEvent, summary: e.target.value })}
                        required
                        data-testid="event-title-input"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-foreground">Inicio</Label>
                        <Input
                          type="datetime-local"
                          className="bg-muted/50 border-transparent focus:border-primary rounded-xl"
                          value={newEvent.start_time}
                          onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                          required
                          data-testid="event-start-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">Fin</Label>
                        <Input
                          type="datetime-local"
                          className="bg-muted/50 border-transparent focus:border-primary rounded-xl"
                          value={newEvent.end_time}
                          onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                          required
                          data-testid="event-end-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Descripción (opcional)</Label>
                      <Input
                        placeholder="Notas adicionales..."
                        className="bg-muted/50 border-transparent focus:border-primary rounded-xl"
                        value={newEvent.description}
                        onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                        data-testid="event-description-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Email invitado (opcional)</Label>
                      <Input
                        type="email"
                        placeholder="cliente@email.com"
                        className="bg-muted/50 border-transparent focus:border-primary rounded-xl"
                        value={newEvent.attendee_email}
                        onChange={(e) => setNewEvent({ ...newEvent, attendee_email: e.target.value })}
                        data-testid="event-attendee-input"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" className="rounded-full bg-primary" disabled={saving} data-testid="event-save-btn">
                        {saving ? "Creando..." : "Crear Evento"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {connections.length === 0 ? (
          <Card className="glass-card border-white/10 max-w-md mx-auto">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <FaGoogle className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Conecta Google Calendar
              </h3>
              <p className="text-muted-foreground mb-6">
                Vincula tus cuentas de Google para que tus agentes puedan agendar citas automáticamente
              </p>
              <Button
                onClick={connectGoogleCalendar}
                className="rounded-full bg-primary hover:bg-primary/90 glow-primary"
                data-testid="connect-calendar-btn"
              >
                <FaGoogle className="w-5 h-5 mr-2" />
                Vincular cuenta de Google
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Calendar */}
            <Card className="glass-card border-white/10">
              <CardContent className="p-4">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-xl"
                  data-testid="calendar-widget"
                />
              </CardContent>
            </Card>

            {/* Events List */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  Eventos del {format(selectedDate, "d 'de' MMMM", { locale: es })}
                </h2>
                {getEventsForDate(selectedDate).length === 0 ? (
                  <Card className="glass-card border-white/10 border-dashed">
                    <CardContent className="p-8 text-center">
                      <LuCalendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No hay eventos para este día</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {getEventsForDate(selectedDate).map((event, index) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card className="glass-card border-white/10 hover:border-primary/30 transition-all" data-testid={`event-${event.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <h3 className="font-medium text-foreground">{event.summary}</h3>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                  <LuClock className="w-4 h-4" />
                                  {formatEventTime(event.start_time)} - {formatEventTime(event.end_time)}
                                </div>
                                {event.description && (
                                  <p className="text-sm text-muted-foreground mt-2">{event.description}</p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                {event.html_link && (
                                  <a href={event.html_link} target="_blank" rel="noopener noreferrer">
                                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary">
                                      <LuExternalLink className="w-4 h-4" />
                                    </Button>
                                  </a>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-muted-foreground hover:text-destructive"
                                  onClick={() => handleDeleteEvent(event.id)}
                                  data-testid={`delete-event-${event.id}`}
                                >
                                  <LuTrash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Upcoming Events */}
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
                  Próximos Eventos
                </h2>
                {events.length === 0 ? (
                  <Card className="glass-card border-white/10 border-dashed">
                    <CardContent className="p-8 text-center">
                      <p className="text-muted-foreground">No hay eventos próximos</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {events.slice(0, 5).map((event, index) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card className="glass-card border-white/10">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                <LuCalendar className="w-5 h-5 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-foreground truncate">{event.summary}</h3>
                                <p className="text-sm text-muted-foreground">
                                  {formatEventDate(event.start_time)} · {formatEventTime(event.start_time)}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CalendarPage;
